import { Stack, Typography } from '@mui/material'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { format } from 'date-fns'
import { useGetVgtTrends } from 'hooks/api/Reports/useGetVgtTrends'
import { VgtTrendsChart } from './VgtTrendsChart'
import { useSetLe } from 'stores/useSetLe'
import { GamingReportsHeader } from '../GamingReportsHeader'
import { useState } from 'react'
import { WeekRangeSelect } from 'components/WeekRangeSelect'
import { getReportStartAndEndDates } from 'utils/util'
import { getLEIdsByPermission } from 'utils/permissions'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useSetMe } from 'stores/useMeQuery'

export const VgtTrendsPage = () => {
  const { passedLe } = useSetLe()
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const dates = getReportStartAndEndDates(8, 1)
  const calcStartDate = dates.startDate
  const endDate = dates.endDate

  const getDateOffset = (days: number) => {
    const date = new Date()
    date.setDate(date.getDate() + days)
    return date
  }
  const [numDays, setNumDays] = useState<string>('56')
  const [startDate, setStartDate] = useState<Date>(calcStartDate)

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'gamingReportsPermission'
  )

  const reportQuery = useGetVgtTrends({
    licensedEstablishmentId: Number(passedLe?.id),
    startDate,
    endDate,
    enabled: !!passedLe?.id,
  })

  const formattedStartDate = format(startDate, 'MM/dd/yyyy')
  const formattedEndDate = format(endDate, 'MM/dd/yyyy')

  interface RawDataItem {
    amount: number
    vgtNumber: string
    date: string
    name: string
    tagNumber: number
  }

  interface TransformedDataItem {
    date: string
    [key: string]: { amount: number; name: string; tagNumber: number } | string
  }

  const transformData = (rawData: RawDataItem[]): TransformedDataItem[] => {
    const groupedByDate: Record<
      string,
      Record<string, { amount: number; name: string; tagNumber: number }>
    > = {}

    rawData.forEach((item) => {
      const formattedDate = format(new Date(item.date), 'yyyy-MM-dd')
      if (!groupedByDate[formattedDate]) {
        groupedByDate[formattedDate] = {}
      }
      groupedByDate[formattedDate][item.vgtNumber] = {
        amount: item.amount,
        name: item.name,
        tagNumber: item.tagNumber,
      }
    })

    return Object.keys(groupedByDate).map((date) => ({
      date,
      ...groupedByDate[date],
    }))
  }

  return (
    <GamingReportsHeader currentTab={'6'}>
      {leIdsByPermission.length !== 0 && (
        <WeekRangeSelect
          label={`Weeks of: ${formattedStartDate} - ${formattedEndDate}`}
          numDays={numDays}
          setStartDate={setStartDate}
          dateOffset={getDateOffset}
          setNumDays={setNumDays}
          displayWeeks={[4, 8, 16]}
        >
          <Stack spacing={4}>
            {reportQuery.isPending && reportQuery.isFetching && (
              <ActivityIndicator />
            )}
            {reportQuery.isError && <ErrorIndicator />}
            {reportQuery.data &&
              (reportQuery.data.metrics.length === 0 ? (
                <Typography variant="h4"></Typography>
              ) : (
                <VgtTrendsChart
                  data={transformData(reportQuery.data.metrics)}
                  endDate={endDate}
                />
              ))}
          </Stack>
        </WeekRangeSelect>
      )}
    </GamingReportsHeader>
  )
}
